/**
 * tdesign v1.6.6
 * (c) 2023 tdesign
 * @license MIT
 */

import { h as helper } from '../_chunks/dep-1461d230.js';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { defineComponent, toRefs, computed, ref, inject, watch, onBeforeUnmount } from '@vue/composition-api';
import props from './props.js';
import '../hooks/index.js';
import { CheckboxGroupInjectionKey } from './constants.js';
import { getCheckboxStore } from './store.js';
import { useKeyboardEvent } from './hooks/useKeyboardEvent.js';
import { useCommonClassName, usePrefixClass } from '../hooks/useConfig.js';
import { useVModel } from '../hooks/useVModel.js';
import { useFormDisabled } from '../hooks/useFormDisabled.js';
import { useElementLazyRender } from '../hooks/useElementLazyRender.js';
import { renderTNodeJSX } from '../utils/render-tnode.js';
import '../hooks/slots.js';
import 'lodash/isArray';
import '../hooks/useCommonClassName.js';
import '../config-provider/useConfig.js';
import '../config-provider/context.js';
import 'lodash/merge';
import 'lodash/mergeWith';
import '../_common/js/global-config/default-config.js';
import '../_common/js/global-config/locale/zh_CN.js';
import '@babel/runtime/helpers/typeof';
import '../_chunks/dep-fdafdce0.js';
import 'dayjs';
import '../_chunks/dep-9849f16c.js';
import '../config-provider/type.js';
import '../hooks/useDefaultValue.js';
import '../hooks/useDestroyOnClose.js';
import '../hooks/useGlobalIcon.js';
import '../hooks/useLazyLoad.js';
import '../_common/js/utils/observe.js';
import '../hooks/useResizeObserver.js';
import '../hooks/useVirtualScrollNew.js';
import '@babel/runtime/helpers/toConsumableArray';
import '../hooks/useImagePreviewUrl.js';
import '../_common/js/upload/utils.js';
import '../_common/js/log/log.js';
import '@babel/runtime/helpers/classCallCheck';
import '@babel/runtime/helpers/createClass';
import 'lodash/kebabCase';
import 'vue';
import 'lodash/camelCase';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var _Checkbox = defineComponent({
  name: "TCheckbox",
  props: _objectSpread(_objectSpread({}, props), {}, {
    stopLabelTrigger: Boolean,
    storeKey: String,
    index: Number,
    data: Object
  }),
  model: {
    prop: "checked",
    event: "change"
  },
  setup: function setup(props2) {
    var _toRefs = toRefs(props2),
      storeKey = _toRefs.storeKey;
    var checkboxStore = computed(function () {
      return getCheckboxStore(storeKey.value);
    });
    var labelRef = ref();
    var _useCommonClassName = useCommonClassName(),
      STATUS = _useCommonClassName.STATUS;
    var checkboxGroupExist = ref(false);
    var _toRefs2 = toRefs(props2),
      checked = _toRefs2.checked,
      indeterminate = _toRefs2.indeterminate,
      disabled = _toRefs2.disabled,
      value = _toRefs2.value,
      lazyLoad = _toRefs2.lazyLoad,
      label = _toRefs2.label,
      data = _toRefs2.data;
    var _useVModel = useVModel(checked, props2.defaultChecked, props2.onChange, "change", "checked"),
      _useVModel2 = _slicedToArray(_useVModel, 2),
      innerChecked = _useVModel2[0],
      setInnerChecked = _useVModel2[1];
    var checkboxGroupData = inject(CheckboxGroupInjectionKey, void 0);
    var tIndeterminate = ref(false);
    var tChecked = ref(false);
    var handleParentCheckedChange = function handleParentCheckedChange(_ref) {
      var parentIsCheckAll = _ref.parentIsCheckAll,
        parentChecked = _ref.parentChecked,
        parentIsIndeterminate = _ref.parentIsIndeterminate;
      var value2 = props2.value,
        checkAll = props2.checkAll;
      if (checkAll) {
        tChecked.value = parentIsCheckAll;
        tIndeterminate.value = parentIsIndeterminate;
      } else {
        tChecked.value = parentChecked.includes(value2);
      }
      checkboxGroupExist.value = checkboxStore.value.parentExist;
    };
    watch([innerChecked, checkboxStore], function () {
      var _checkboxStore$value;
      if ((_checkboxStore$value = checkboxStore.value) !== null && _checkboxStore$value !== void 0 && _checkboxStore$value.parentExist) {
        checkboxGroupExist.value = true;
      } else {
        tChecked.value = innerChecked.value;
      }
    }, {
      immediate: true
    });
    watch([indeterminate, checkboxStore], function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
        val = _ref3[0],
        checkboxStore2 = _ref3[1];
      if (!(checkboxStore2 !== null && checkboxStore2 !== void 0 && checkboxStore2.parentExist)) {
        tIndeterminate.value = val;
      }
    }, {
      immediate: true
    });
    var tName = ref();
    var tDisabled = ref();
    var _useFormDisabled = useFormDisabled(),
      formDisabled = _useFormDisabled.formDisabled;
    var handleParentDisabled = function handleParentDisabled(_ref4) {
      var parentDisabled = _ref4.parentDisabled,
        parentMaxExceeded = _ref4.parentMaxExceeded;
      var checkAll = props2.checkAll,
        disabled2 = props2.disabled;
      if (!checkAll && !tChecked.value && parentMaxExceeded) {
        tDisabled.value = true;
        return;
      }
      if (disabled2 !== void 0) {
        tDisabled.value = disabled2;
        return;
      }
      if (parentDisabled !== void 0) {
        tDisabled.value = parentDisabled;
      }
    };
    watch([disabled, checkboxStore], function (_ref5) {
      var _checkboxStore$value2;
      var _ref6 = _slicedToArray(_ref5, 1),
        val = _ref6[0];
      if (!((_checkboxStore$value2 = checkboxStore.value) !== null && _checkboxStore$value2 !== void 0 && _checkboxStore$value2.parentExist)) {
        tDisabled.value = val;
      }
    }, {
      immediate: true
    });
    var COMPONENT_NAME = usePrefixClass("checkbox");
    var labelClasses = ref([]);
    watch([tChecked, tIndeterminate], function (_ref7) {
      var _ref9;
      var _ref8 = _slicedToArray(_ref7, 2),
        tChecked2 = _ref8[0],
        tIndeterminate2 = _ref8[1];
      labelClasses.value = ["".concat(COMPONENT_NAME.value), (_ref9 = {}, _defineProperty(_ref9, STATUS.value.checked, tChecked2), _defineProperty(_ref9, STATUS.value.indeterminate, tIndeterminate2), _ref9)];
    }, {
      immediate: true
    });
    var subscribeParentData = function subscribeParentData(val) {
      checkboxStore.value.subscribe(val, function (data2) {
        if (data2.type === "checked") {
          handleParentCheckedChange(data2);
        } else if (data2.type === "checkbox") {
          handleParentDisabled(data2);
          if (data2.checkboxName) {
            tName.value = data2.checkboxName;
          }
        }
      });
    };
    watch([data, label, storeKey], function () {
      if (!storeKey.value) return;
      subscribeParentData(props2.checkAll ? "CHECK_ALL" : value.value);
    }, {
      immediate: true
    });
    onBeforeUnmount(function () {
      var _checkboxStore$value3;
      (_checkboxStore$value3 = checkboxStore.value) === null || _checkboxStore$value3 === void 0 || _checkboxStore$value3.unSubscribe(props2.checkAll ? "CHECK_ALL" : value.value);
    });
    var handleChange = function handleChange(e) {
      if (props2.readonly) return;
      var checked2 = !tChecked.value;
      setInnerChecked(checked2, {
        e: e
      });
      if (checkboxGroupData !== null && checkboxGroupData !== void 0 && checkboxGroupData.value.handleCheckboxChange) {
        checkboxGroupData.value.onCheckedChange({
          checked: checked2,
          checkAll: props2.checkAll,
          e: e,
          option: props2
        });
      }
    };
    var handleLabelClick = function handleLabelClick(e) {
      if (props2.stopLabelTrigger) e.preventDefault();
    };
    var _useElementLazyRender = useElementLazyRender(labelRef, lazyLoad),
      showCheckbox = _useElementLazyRender.showElement;
    var _useKeyboardEvent = useKeyboardEvent(handleChange),
      onCheckboxFocus = _useKeyboardEvent.onCheckboxFocus,
      onCheckboxBlur = _useKeyboardEvent.onCheckboxBlur;
    return {
      labelRef: labelRef,
      labelClasses: labelClasses,
      COMPONENT_NAME: COMPONENT_NAME,
      tDisabled: tDisabled,
      tIndeterminate: tIndeterminate,
      tName: tName,
      tChecked: tChecked,
      showCheckbox: showCheckbox,
      formDisabled: formDisabled,
      STATUS: STATUS,
      checkboxGroupExist: checkboxGroupExist,
      handleChange: handleChange,
      handleLabelClick: handleLabelClick,
      onCheckboxFocus: onCheckboxFocus,
      onCheckboxBlur: onCheckboxBlur
    };
  },
  render: function render() {
    var _this$tDisabled;
    var h = arguments[0];
    var disabled = (_this$tDisabled = this.tDisabled) !== null && _this$tDisabled !== void 0 ? _this$tDisabled : this.formDisabled;
    var classes = this.labelClasses.concat(_defineProperty({}, this.STATUS.disabled, disabled));
    var slotsPrams = {
      data: this.data,
      index: this.index
    };
    return h("label", {
      "ref": "labelRef",
      "class": classes,
      "attrs": {
        "tabindex": disabled ? void 0 : "0"
      },
      "on": {
        "focus": this.onCheckboxFocus,
        "blur": this.onCheckboxBlur
      }
    }, [!this.showCheckbox ? null : [h("input", helper([{
      "attrs": {
        "type": "checkbox",
        "disabled": disabled,
        "readonly": this.readonly,
        "indeterminate": this.tIndeterminate,
        "name": this.tName || this.name || void 0
      },
      "class": "".concat(this.COMPONENT_NAME, "__former"),
      "domProps": {
        "value": this.value ? this.value : void 0,
        "checked": this.tChecked
      }
    }, {
      "on": {
        change: this.handleChange
      }
    }, {
      "key": "input",
      "attrs": {
        "tabindex": "-1"
      }
    }])), h("span", {
      "class": "".concat(this.COMPONENT_NAME, "__input"),
      "key": "input-span"
    }), h("span", {
      "class": "".concat(this.COMPONENT_NAME, "__label"),
      "key": "label",
      "on": {
        "click": this.handleLabelClick
      }
    }, [renderTNodeJSX(this, "default", {
      params: slotsPrams
    }) || renderTNodeJSX(this, "label", {
      params: slotsPrams,
      slotFirst: this.checkboxGroupExist
    })])]]);
  }
});

export { _Checkbox as default };
//# sourceMappingURL=checkbox.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/tdesign-vue/es/checkbox/checkbox.js
// module id = null
// module chunks = 